// src/data/officeCopy.js
//
// WO-001 A3.2 - the outgoing officer's records (2026-09-14, task list section 246). Bob's ruling,
// verbatim, design A3.2: "An outgoing General Chair may transfer any digital records he deems
// necessary or important to keep for his own records - however, each document and every page of
// each document must have a waterline shaded 20% and the watermark must be large, bold 'GC COPY'
// with a date stamp (date copy made)." A3.6, Bob: "For the same reasoning as the GC, yes" - an
// outgoing Local Chairperson takes the same copy and it reads 'LC COPY'. "The mark names the office
// of the term being exported."
//
// Every requirement is mandatory and none is configurable (A3.2): every document, EVERY PAGE, large
// bold mark, 20% shade, the date the copy was made (not the document's date), applied at generation
// (never a viewer overlay), and the export is itself an audit event - who, what, when, under which
// term. "A failure to mark is a failure to export": a file this platform cannot stamp page by page
// (anything that is not a PDF or an image) is NOT exported and is listed as refused, by name.
//
// This module is the decision: who may, what is in scope, what the mark says, what is stamped and
// what is refused, the audit row. The Cloud Function exportOfficeCopy (functions\index.js) does the
// stamping with pdf-lib and the storage; projected into functions\shared\ by sync-shared-modules.js.
import { officeHistory } from './officeSuccession';

export const COPY_MARK = { general_committee: 'GC COPY', local_committee: 'LC COPY' };
export const COPY_OPACITY = 0.2;                       // "waterline shaded 20%"
export const STAMPABLE = { 'application/pdf': 'pdf', 'image/png': 'png', 'image/jpeg': 'jpg', 'image/jpg': 'jpg' };

export function markFor(office) {
  return (office && COPY_MARK[office.type]) || null;
}

// Who may take the copy: the holder of the term, for his own term (open or closed - the closing is
// when it is needed, but the right is his either way and the act is audited), or the administrator.
export function mayExportCopy(caller, term) {
  if (!caller || !term) return { ok: false, reason: 'no caller or term' };
  if (caller.isAdmin || caller.role === 'admin') return { ok: true, as: 'admin' };
  if (caller.uid && term.holderUid === caller.uid) return { ok: true, as: 'holder' };
  return { ok: false, reason: 'only the holder of term ' + (term.termId || '') + ' (or the administrator) takes its copy' };
}

const dayOf = (v) => {
  if (!v) return null;
  if (typeof v === 'string') return v.slice(0, 10);
  if (typeof v.toDate === 'function') return v.toDate().toISOString().slice(0, 10);
  if (typeof v.seconds === 'number') return new Date(v.seconds * 1000).toISOString().slice(0, 10);
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return null;
};

// What is his to take: the claims of the office he held, that existed during his term. A claim filed
// after the term ended is the successor's record, not his; a DRAFT is its author's alone (ruling 56)
// and is never in anyone's copy. A claim with no committee/scope match is not the office's.
export function copyScope(term, office, claims) {
  const list = Array.isArray(claims) ? claims : [];
  const start = dayOf(term && term.startedOn), end = dayOf(term && term.endedOn);
  const inScope = [], outOfScope = [];
  for (const c of list) {
    if (!c || !c.id) continue;
    const committee = c.committee || c.generalCommittee || null;
    const mine = office.type === 'general_committee' ? committee === office.officeId : c.scopeKey === office.officeId;
    const created = dayOf(c.filedAt) || dayOf(c.createdAt);
    let why = null;
    if (!mine) why = 'not a record of ' + office.officeId;
    else if (c.status === 'draft') why = 'a draft is its author\'s alone [R-56]';
    else if (end && created && created > end) why = 'filed ' + created + ', after the term ended ' + end;
    else if (!created) why = 'no filing date on the record - cannot be placed inside the term';
    (why ? outOfScope : inScope).push(why ? { id: c.id, why } : c);
  }
  void start;
  return { inScope, outOfScope };
}

// The stamp, once per export: the same text and date on every page of every document.
export function stampSpec(office, term, at) {
  const when = at || new Date().toISOString();
  const mark = markFor(office);
  if (!mark) return { ok: false, errors: ['no mark for an office of type ' + (office && office.type)] };
  return {
    ok: true, errors: [],
    text: mark, dateStamp: 'Copy made ' + when.slice(0, 10), opacity: COPY_OPACITY,
    footer: mark + ' - ' + (term && term.holderName ? term.holderName + ', ' : '') + (office.label || office.officeId) + ' - term ' + (term && term.termId ? term.termId : '?') + ' - copy made ' + when.slice(0, 10) + ' - not the platform\'s record',
    at: when,
  };
}

// The claim record itself, as lines for a summary page. Fields of the record, named; nothing composed.
export function claimSummaryLines(claim) {
  const c = claim || {};
  const v = (x) => (x === null || x === undefined || x === '') ? '[not on record]' : (typeof x === 'object' ? (dayOf(x) || JSON.stringify(x)) : String(x));
  const lines = [
    'CLAIM ' + v(c.id),
    'Status: ' + v(c.status),
    'Claimant: ' + v(c.claimantName),
    'Committee: ' + v(c.committee || c.generalCommittee) + '   Territory: ' + v(c.scopeKey),
    'Railroad: ' + v(c.railroad) + '   Agreement: ' + v(c.applicableAgreement),
    'Incident date: ' + v(c.incidentDate) + '   Location: ' + v(c.workLocation || c.eventLocation),
    'Filed: ' + v(dayOf(c.filedAt) || dayOf(c.createdAt)) + '   Filing window: ' + v(dayOf(c.expiresAt)),
    'Amount claimed: ' + v(c.amountClaimed),
    'Presented on: ' + v(c.presentedOn) + '   Outcome: ' + v(c.outcome),
    '',
    'STATEMENT OF CLAIM (verbatim from the record)',
  ];
  const desc = String(c.description || '').trim() || '[not on record]';
  desc.split(/\r?\n/).forEach(l => lines.push(l));
  const rules = Array.isArray(c.matchedRules) ? c.matchedRules.filter(r => r && !r.setAsideAt) : [];
  lines.push('', 'RULE(S) RELIED ON: ' + (rules.length ? rules.map(r => r.rule || r.ruleId).join('; ') : '[none on record]'));
  const docs = Array.isArray(c.documents) ? c.documents : [];
  lines.push('', 'DOCUMENTS ON THE RECORD: ' + docs.length);
  docs.forEach(d => lines.push('  - ' + v(d.name) + (d.type ? ' (' + d.type + ')' : '') + (d.uploadedAt ? ' uploaded ' + dayOf(d.uploadedAt) : '')));
  return lines;
}

// The manifest: what will be stamped, what is refused and why. Every document of every in-scope
// claim is decided here, before a byte is read.
export function exportManifest(claims) {
  const stamp = [], refused = [];
  for (const c of (claims || [])) {
    stamp.push({ claimId: c.id, kind: 'summary', name: c.id + '-record.pdf' });
    for (const d of (Array.isArray(c.documents) ? c.documents : [])) {
      const k = d && d.fileType ? STAMPABLE[String(d.fileType).toLowerCase()] : null;
      if (k && d.storagePath) stamp.push({ claimId: c.id, kind: k, name: d.name || d.storagePath.split('/').pop(), storagePath: d.storagePath });
      else refused.push({ claimId: c.id, name: (d && d.name) || '(unnamed)', why: !d || !d.storagePath ? 'no file behind the record' : 'a ' + (d.fileType || 'file of unknown type') + ' cannot be marked page by page here - a failure to mark is a failure to export' });
    }
  }
  return { stamp, refused };
}

export function copyAudit(office, term, caller, spec, result) {
  return {
    action: 'export_office_copy', officeId: office.officeId, termId: term.termId, holder: { uid: term.holderUid, name: term.holderName },
    actor: caller.uid, as: result && result.as || null, mark: spec.text, copyMadeOn: spec.at.slice(0, 10),
    claims: result ? result.claims : 0, filesStamped: result ? result.stamped : 0, pagesStamped: result ? result.pages : 0, filesRefused: result ? result.refused : 0,
    at: spec.at,
  };
}

// The term to export, by id, checked against the office it names.
export function termForCopy(terms, termId) {
  const t = (terms || []).find(x => x && x.termId === termId) || null;
  if (!t) return { ok: false, errors: ['term ' + termId + ' is not on record'] };
  return { ok: true, errors: [], term: t, chain: officeHistory(terms, t.officeId) };
}
